/**
 * EditableContentTypesSection - Content Types cards with element-level editing support
 * 
 * Each element (heading, content type cards) is wrapped with SelectableElement
 * for click-to-select in the Page Builder Studio.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import { BookOpen, Pen, Palette, Eye, Upload, Zap, Shield, Users } from 'lucide-react';
import SelectableElement from './SelectableElement';
import InlineTextEditor from './InlineTextEditor';
import { 
  LANDING_CONTENT_TYPES_ELEMENTS,
  getElementValue,
  getElementStyles,
  getElementMotion,
} from './ElementRegistry';

// Easing CSS values
const EASING_VALUES = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  calm: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

// Icon mapping
const ICON_MAP = {
  BookOpen, Pen, Palette, Eye, Upload, Zap, Shield, Users
};

// Element animation wrapper
const AnimatedElement = ({ 
  children, 
  motion,
  animationKey,
  disabled,
  className = '' 
}) => {
  const ref = useRef(null);
  const prevKeyRef = useRef(animationKey);
  
  const { durationMs = 700, delayMs = 0, easing = 'smooth', fromOpacity = 0, fromY = 30, fromScale = 1 } = motion || {};
  const easingValue = EASING_VALUES[easing] || EASING_VALUES.smooth;

  // Handle animation replay
  useEffect(() => {
    if (animationKey !== prevKeyRef.current && ref.current) {
      prevKeyRef.current = animationKey;
      ref.current.style.opacity = fromOpacity;
      ref.current.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
      
      void ref.current.offsetWidth;
      
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = 'translateY(0) scale(1)';
        }
      }, 50);
    }
  }, [animationKey, fromOpacity, fromY, fromScale]);

  // Initial animation via intersection observer
  useEffect(() => {
    if (disabled || !ref.current) return;
    
    const el = ref.current;
    el.style.opacity = fromOpacity;
    el.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
    el.style.transition = `opacity ${durationMs}ms ${easingValue} ${delayMs}ms, transform ${durationMs}ms ${easingValue} ${delayMs}ms`;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0) scale(1)';
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, durationMs, delayMs, easingValue, fromOpacity, fromY, fromScale]);

  if (disabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

// Text style class helper
const getTextStyleClass = (styles) => {
  const sizeMap = {
    'xs': 'text-xs', 'sm': 'text-sm', 'base': 'text-base', 'lg': 'text-lg',
    'xl': 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl',
  };
  const weightMap = {
    'light': 'font-light', 'normal': 'font-normal', 'medium': 'font-medium',
    'semibold': 'font-semibold', 'bold': 'font-bold',
  };
  const alignMap = { 'left': 'text-left', 'center': 'text-center', 'right': 'text-right' };
  
  return `${sizeMap[styles?.size] || 'text-base'} ${weightMap[styles?.weight] || ''} ${alignMap[styles?.alignment] || 'text-center'}`;
};

const EditableContentTypesSection = ({ 
  props = {},
  selectedElementId,
  onSelectElement,
  editingElementId,
  onStartEditing,
  onStopEditing,
  onUpdateElementValue,
  animationKey = 0,
  disabled = false,
}) => {
  // Build elements map
  const elements = useMemo(() => {
    const map = {};
    LANDING_CONTENT_TYPES_ELEMENTS.forEach(el => { map[el.id] = el; });
    return map;
  }, []);

  const getValue = (elementId) => {
    const element = elements[elementId];
    if (!element) return '';
    return getElementValue(props, element);
  };

  const getStyles = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementStyles(props, element);
  };

  const getMotion = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementMotion(props, element);
  };

  const handleSelectElement = (elementId) => {
    onSelectElement?.(elementId);
  };

  const handleDoubleClick = (elementId) => {
    onStartEditing?.(elementId);
  };

  const handleSaveText = (elementId, newValue) => {
    onUpdateElementValue?.(elementId, newValue);
    onStopEditing?.();
  };

  // Safe string coercion - ensures we never render objects as React children
  const safeString = (val, fallback = '') => {
    if (!val) return fallback;
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      if (val.title) return val.title;
      if (val.label) return val.label;
      if (val.text) return val.text;
    }
    return fallback;
  };

  // Get heading values
  const headingValue = safeString(getValue('content_types.heading'), 'Your format. Your vision.');
  const headingStyles = getStyles('content_types.heading');
  const headingMotion = getMotion('content_types.heading');

  // Default content types items
  const defaultItems = [
    { label: 'Comics & Manga', description: 'Multi-page stories with full reader experience', icon: 'BookOpen' },
    { label: 'Novels & Fiction', description: 'Chapter-based written content with rich formatting', icon: 'Pen' },
    { label: 'Art & Illustration', description: 'Standalone artwork with full gallery features', icon: 'Palette' }
  ];

  // Get items from props or defaults
  const items = props.types || props.items || defaultItems;

  return (
    <section className="py-24 bg-zinc-950" data-testid="content-types-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Heading */}
        <AnimatedElement
          motion={headingMotion}
          animationKey={animationKey}
          disabled={disabled}
          className="text-center mb-16"
        >
          <SelectableElement
            elementId="content_types.heading"
            elementLabel="Section Heading"
            elementType="text"
            isSelected={selectedElementId === 'content_types.heading'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <h2 
              className={`text-white mb-4 ${getTextStyleClass(headingStyles)}`}
              style={{ opacity: headingStyles.opacity ?? 1 }}
              onDoubleClick={() => handleDoubleClick('content_types.heading')}
            >
              {editingElementId === 'content_types.heading' ? (
                <InlineTextEditor
                  value={headingValue}
                  isEditing={true}
                  maxLength={60}
                  onSave={(val) => handleSaveText('content_types.heading', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                headingValue
              )}
            </h2>
          </SelectableElement>
        </AnimatedElement>
        
        {/* Content Type Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {items.map((item, index) => {
            const elementId = `content_types.item_${index}`;
            const itemMotion = getMotion(elementId);
            const itemStyles = getStyles(elementId);
            const itemValue = getValue(elementId) || item;
            
            // Normalize item data
            const normalizedItem = typeof itemValue === 'object' ? itemValue : item;
            const IconComponent = ICON_MAP[normalizedItem.icon] || BookOpen;
            
            return (
              <AnimatedElement 
                key={`content-type-${index}`}
                motion={itemMotion}
                animationKey={animationKey}
                disabled={disabled}
              >
                <SelectableElement
                  elementId={elementId}
                  elementLabel={`Content Type ${index + 1}`}
                  elementType="card"
                  isSelected={selectedElementId === elementId}
                  onSelect={handleSelectElement}
                  disabled={disabled}
                >
                  <div 
                    className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 text-center hover:border-orange-500/50 transition-colors h-full"
                    style={{ opacity: itemStyles.opacity ?? 1 }}
                    onDoubleClick={() => handleDoubleClick(elementId)}
                  >
                    <div className="w-16 h-16 mx-auto mb-6 rounded-xl bg-orange-500/10 flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-orange-400" />
                    </div>
                    {editingElementId === elementId ? (
                      <div className="space-y-2">
                        <InlineTextEditor
                          value={normalizedItem.label}
                          isEditing={true}
                          maxLength={40}
                          onSave={(val) => handleSaveText(elementId, { ...normalizedItem, label: val })}
                          onCancel={onStopEditing}
                          className="text-xl font-semibold text-white block"
                        />
                      </div>
                    ) : (
                      <>
                        <h3 className="text-xl font-semibold text-white mb-3">{normalizedItem.label}</h3>
                        <p className="text-zinc-400">{normalizedItem.description}</p>
                      </>
                    )}
                  </div>
                </SelectableElement>
              </AnimatedElement>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EditableContentTypesSection;
